import { useEffect, useState } from "react";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import type { AdminQueryError } from "../../contracts/admin-query-errors";
import { ADMIN_DEFAULT_TIME_RANGE_DAYS } from "../../contracts/admin-read-models";
import type {
  AdminProjectionFreshness,
  AdminOverviewQuery,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { useAdminAutoRefreshTick } from "../../shared/query/admin-auto-refresh";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import { loadOverviewSummary } from "./overview-use-cases";
import { toOverviewViewModel, type OverviewViewModel } from "./overview-view-model";

export type OverviewScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  query?: AdminOverviewQuery;
};

export type OverviewScreenModel = AdminScreenState<OverviewViewModel>;

function createDefaultOverviewQuery(): AdminOverviewQuery {
  const now = new Date();
  return {
    timeRange: {
      from: new Date(now.getTime() - ADMIN_DEFAULT_TIME_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      to: now.toISOString(),
    },
  };
}

function deriveFreshnessPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "stale" | "partial" | "ready" {
  const values = Object.values(sourceFreshness);

  if (values.includes("stale")) {
    return "stale";
  }

  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }

  return "ready";
}

function isOverviewEmpty(viewModel: OverviewViewModel): boolean {
  return (
    viewModel.requestCountLabel === "0" &&
    viewModel.topErrorCodes.length === 0 &&
    viewModel.useCaseHealth.length === 0 &&
    viewModel.jobHealth.length === 0 &&
    viewModel.releaseHealth.length === 0
  );
}

export async function resolveOverviewScreenModel(
  dependencies: OverviewScreenModelDependencies,
): Promise<OverviewScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.overview.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "Overview read permission denied.",
        ko: "개요 조회 권한이 없습니다.",
      },
      "admin_overview_permission_denied",
    );
  }

  try {
    const envelope = await loadOverviewSummary(
      dependencies.queryPort,
      dependencies.query ?? createDefaultOverviewQuery(),
    );
    const viewModel = toOverviewViewModel(envelope.data, envelope.meta, dependencies.locale ?? "ko");

    if (isOverviewEmpty(viewModel)) {
      return {
        status: "empty",
      };
    }

    const freshnessPriority = deriveFreshnessPriority(envelope.meta.sourceFreshness);

    if (freshnessPriority === "stale") {
      return {
        status: "stale",
        viewModel,
      };
    }

    if (freshnessPriority === "partial") {
      return {
        status: "partial",
        viewModel,
      };
    }

    return {
      status: "ready",
      viewModel,
    };
  } catch (error) {
    return toAdminScreenErrorState(dependencies.locale ?? "ko", error);
  }
}

export function useOverviewScreenModel(
  dependencies: OverviewScreenModelDependencies,
): OverviewScreenModel {
  const [state, setState] = useState<OverviewScreenModel>(createIdleAdminScreenState());
  const refreshTick = useAdminAutoRefreshTick(dependencies.runtimeConfig);

  useEffect(() => {
    let cancelled = false;

    setState((current) => createLoadingAdminScreenState(current.viewModel));

    void resolveOverviewScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dependencies, refreshTick]);

  return state;
}
